import { ChangeEvent, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import LoginLayout from '../../components/LoginLayout';
import Input from '../../widgets/Input';
import Button from '../../widgets/Button';
import { sendCode } from '../../../shared/service/auth';
import { IResetPassword } from '../../../shared/types/ResetPassword';
import {
  MAX_RETRY_CODE,
  ResetScreens,
} from '../../../shared/constants/ResetPassword';
import useTracking from '../../../utilities/hooks/useTracking';
import { EventActions, EventNames } from '../../../shared/constants/analytics';

function EmailScreen({ state, setState }: IResetPassword) {
  const { email } = state;
  const [maxApiError, setMaxApiError] = useState('');

  const { trackEvent } = useTracking();

  const isEmailValid = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const { mutate: refetchSendCode, isPending } = useMutation({
    mutationKey: ['requestCode'],
    mutationFn: () => sendCode(email),
    onSuccess: (res) =>
      setState({ currentScreen: ResetScreens.RESET_PASSWORD_SCREEN }),
    onError: (error: any) => {
      if (error.status == MAX_RETRY_CODE) {
        setMaxApiError(
          'You’ve reached the limit for password change attempts today. Please try again tomorrow',
        );
      }
    },
  });

  function handleSubmit(e: ChangeEvent<HTMLFormElement>) {
    e.preventDefault();
    refetchSendCode();
  }

  return (
    <LoginLayout
      title="Reset your password"
      subTitle="Enter your email ID and we’ll send you a secure magic code to reset your password"
      currentState={state.currentScreen}
      setState={setState}
      backButton
    >
      <form className="mt-6 grid gap-5" onSubmit={handleSubmit}>
        <div>
          <Input
            label="Email"
            name="email"
            value={email}
            onChange={(e) => setState({ email: e.target.value })}
          />
          {maxApiError && (
            <p className="text-redd-600 mt-2 text-sm">{maxApiError}</p>
          )}
        </div>

        <div className="mt-8 grid gap-y-4">
          <Button
            type="submit"
            size="lg"
            disabled={!isEmailValid(email)}
            isLoading={isPending}
            className="font-semibold"
            onClick={() =>
              trackEvent(EventNames.WellBeingDashboard, {
                eventLabel: 'send_code',
                eventAction: EventActions.click,
                eventCategory: 'send_code',
                eventSource: 'reset_password',
              })
            }
          >
            Send code
          </Button>
        </div>
      </form>
    </LoginLayout>
  );
}

export default EmailScreen;
